// Lista de cotacoes: abas por estagio, fila de prioridade (setas ▲▼ por linha),
// menu de acoes por linha (mover de estagio / excluir), "+/-" que mostra os produtos sem abrir
// a cotacao e atalho pra Compra Informativa.
import { Fragment, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Minus, Search, FileText, Trash2, MoreVertical, ChevronUp, ChevronDown, ClipboardList, ShoppingCart } from 'lucide-react';
import { api, ApiError } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { useToast } from '../../components/Toast';
import { ConfirmModal } from '../../components/ConfirmModal';
import { FotoProduto } from '../../components/FotoProduto';
import { paraDataMs } from './CotacaoRespostas';
import { useStickyHeader } from '../../lib/useStickyHeader';
import { formatDataSP, formatMoedaSemSimbolo, formatQtd } from '@fiodecobre/shared';

interface Cotacao {
  id: number;
  numero: string;
  status: string;
  observacoes: string | null;
  comprador_nome: string | null;
  total_itens: string;
  total_fornecedores: string;
  data_abertura: string;
  data_validade: string | null;
  ordem: number;
  criado_em: string;
  tipo: string; // 'comparativa' | 'informativa' (DA-309)
}

// Linha de GET /:id/itens-resumo (a rota tambem devolve as respostas; aqui so o produto).
interface ItemPreview {
  id: number;
  codigo: string | null;
  descricao: string;
  quantidade: string;
  unidade: string;
  foto_url: string | null;
  ultima_compra_em: string | null;
  /** A linha amarela da grade (menor unitario com DIFAL, ou a compra forcada); null = sem preco. */
  vencedor: {
    embalagem: string;
    quantidade_embalagem: number;
    quantidade_cotada: number;
    preco_emb: number;
    unitario: number;
  } | null;
}

// A comparativa abre na matriz de comparacao; a informativa, na lista chapada.
const rotaDaCotacao = (c: Cotacao) =>
  c.tipo === 'informativa' ? `/cotacoes/${c.id}/informativa` : `/cotacoes/${c.id}/respostas`;

const STATUS_TABS: Array<{ key: string; label: string }> = [
  { key: 'aberta', label: 'Aberta' },
  { key: 'em_analise', label: 'Em Analise' },
  { key: 'aprovada', label: 'Aprovada' },
  { key: 'finalizada', label: 'Finalizada' },
  { key: 'cancelada', label: 'Cancelada' },
];

const STATUS_LABELS: Record<string, { label: string; cls: string }> = {
  aberta: { label: 'Aberta', cls: 'badge-amarelo' },
  em_analise: { label: 'Em Analise', cls: 'badge-azul' },
  aprovada: { label: 'Aprovada', cls: 'badge-verde' },
  finalizada: { label: 'Finalizada', cls: 'badge-cinza' },
  cancelada: { label: 'Cancelada', cls: 'badge-vermelho' },
};

export function CotacoesHome() {
  const navigate = useNavigate();
  const { usuario } = useAuth();
  const isAdmin = usuario?.perfil === 'admin';
  const toast = useToast();

  const [lista, setLista] = useState<Cotacao[]>([]);
  const [loading, setLoading] = useState(true);
  const [busca, setBusca] = useState('');
  const [filtroStatus, setFiltroStatus] = useState<string>('aberta');
  const [confirmarRemover, setConfirmarRemover] = useState<Cotacao | null>(null);
  const [selecionadaId, setSelecionadaId] = useState<number | null>(null);
  const [menuAberto, setMenuAberto] = useState<{ id: number; top: number; left: number; abrirCima: boolean } | null>(null);
  const buscaTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [expandidos, setExpandidos] = useState<Set<number>>(new Set());
  // null = carregando; [] = falhou ou vazia. Rebusca a cada abertura: a cotacao muda por dentro.
  const [itensPreview, setItensPreview] = useState<Record<number, ItemPreview[] | null>>({});

  async function toggleExpand(id: number) {
    if (expandidos.has(id)) {
      setExpandidos((prev) => { const s = new Set(prev); s.delete(id); return s; });
      return;
    }
    setExpandidos((prev) => new Set(prev).add(id));
    setItensPreview((prev) => ({ ...prev, [id]: prev[id] ?? null }));
    try {
      const r = await api.get<ItemPreview[]>(`/api/cotacoes/${id}/itens-resumo`);
      setItensPreview((prev) => ({ ...prev, [id]: Array.isArray(r) ? r : [] }));
    } catch {
      setItensPreview((prev) => ({ ...prev, [id]: [] }));
    }
  }

  useEffect(() => {
    if (!menuAberto) return;
    const fechar = () => setMenuAberto(null);
    window.addEventListener('click', fechar);
    window.addEventListener('scroll', fechar, true);
    return () => { window.removeEventListener('click', fechar); window.removeEventListener('scroll', fechar, true); };
  }, [menuAberto]);

  async function mudarStatus(c: Cotacao, novo: string) {
    setMenuAberto(null);
    try {
      await api.patch(`/api/cotacoes/${c.id}/status`, { status: novo });
      const lbl = STATUS_LABELS[novo]?.label ?? novo;
      toast.sucesso(`${c.numero} movida para ${lbl}`);
      await carregar();
    } catch (e: unknown) {
      toast.erro(e instanceof ApiError ? e.message : 'Erro ao mudar status');
    }
  }

  async function carregar() {
    try {
      setLoading(true);
      const r = await api.get<Cotacao[]>('/api/cotacoes', {
        query: { q: busca || undefined, status: filtroStatus || undefined },
      });
      setLista(r);
    } catch (e: any) {
      toast.erro(e.message);
    } finally { setLoading(false); }
  }

  useEffect(() => {
    if (buscaTimer.current) clearTimeout(buscaTimer.current);
    buscaTimer.current = setTimeout(carregar, 300);
    return () => { if (buscaTimer.current) clearTimeout(buscaTimer.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [busca, filtroStatus]);

  // Fila de prioridade de compra: as setas reindexam a aba inteira (1..N) e gravam.
  // So faz sentido com a lista completa da aba na tela — com busca ativa a lista e um
  // recorte, e reindexar o recorte bagunçaria a posicao das linhas escondidas.
  const podeReordenar = busca.trim() === '';

  async function moverCotacao(idx: number, delta: number) {
    const destino = idx + delta;
    if (destino < 0 || destino >= lista.length) return;
    const novos = [...lista];
    const [movida] = novos.splice(idx, 1);
    if (!movida) return;
    novos.splice(destino, 0, movida);
    setLista(novos);
    try {
      await api.patch('/api/cotacoes/reordenar', {
        ordem: novos.map((c, i) => ({ id: c.id, ordem: i + 1 })),
      });
    } catch (e: unknown) {
      toast.erro(e instanceof ApiError ? e.message : 'Erro ao reordenar');
      await carregar();
    }
  }

  async function remover(c: Cotacao) {
    try {
      await api.delete(`/api/cotacoes/${c.id}`);
      toast.sucesso(`Cotacao ${c.numero} excluida`);
      setConfirmarRemover(null);
      await carregar();
    } catch (e: unknown) {
      toast.erro(e instanceof ApiError ? e.message : 'Erro ao excluir');
    }
  }

  const sticky = useStickyHeader();

  return (
    <div className="modulo-pagina">
      <div ref={sticky.headerRef} style={sticky.headerStyle}>
        <div className="pagina-header">
          <h2 className="pagina-titulo">
            <FileText size={20} style={{ display: 'inline', marginRight: 6, marginBottom: -3 }} /> Cotacoes
          </h2>
          <div className="pagina-acoes">
            <button
              type="button"
              className="btn-secundario"
              onClick={() => navigate('/cotacoes/falta-comprar')}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
              title="Junta os itens de todas as cotacoes abertas numa lista so — o que ainda falta comprar"
            >
              <ShoppingCart size={16} /> O que falta comprar
            </button>
            <button
              type="button"
              className="btn-primario"
              onClick={() => navigate('/cotacoes/nova')}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
            >
              <Plus size={16} /> Nova Cotacao
            </button>
            <button
              type="button"
              className="btn-secundario"
              onClick={() => navigate('/cotacoes/informativa/nova')}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
              title="Fornecedor unico, sem comparacao de preco — so registra o que esta sendo comprado"
            >
              <ClipboardList size={16} /> Compra Informativa
            </button>
          </div>
        </div>

        {/* Tabs por status */}
        <div role="tablist" style={{ display: 'flex', gap: 4, borderBottom: '1px solid var(--page-border, #e5e7eb)', marginTop: 8, padding: '0 16px' }}>
          {STATUS_TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={filtroStatus === t.key}
              onClick={() => setFiltroStatus(t.key)}
              style={{
                background: filtroStatus === t.key ? 'var(--panel-bg)' : 'transparent',
                color: filtroStatus === t.key ? 'var(--text-color)' : 'var(--text-muted, #6b7280)',
                border: 'none',
                borderBottom: filtroStatus === t.key ? '2px solid var(--primary)' : '2px solid transparent',
                padding: '8px 14px',
                fontSize: 14,
                fontWeight: filtroStatus === t.key ? 600 : 500,
                cursor: 'pointer',
              }}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Busca */}
        <div style={{ position: 'relative', maxWidth: 480, padding: '8px 16px 0' }}>
          <Search size={16} style={{ position: 'absolute', left: 26, top: 17, color: 'var(--text-muted, #6b7280)' }} />
          <input
            type="text"
            placeholder="Buscar por numero, observacao ou comprador"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            style={{ width: '100%', padding: '8px 10px 8px 32px', borderRadius: 4, border: '1px solid var(--input-border, #d1d5db)', background: 'var(--input-bg)', color: 'var(--text-color)' }}
          />
        </div>
      </div>

      <div style={{ padding: '12px 16px 16px' }}>
        {/* Lista */}
        <div style={{ background: 'var(--panel-bg)', borderRadius: 6, border: '1px solid var(--panel-border, #e5e7eb)' }}>
          <table style={{ width: '100%', fontSize: 14, ...sticky.tableStyle }}>
            <thead>
              <tr>
                <th style={{ ...sticky.thStyle, padding: '8px 4px', textAlign: 'center', fontWeight: 600, width: 34, color: 'var(--grid-header-fg)' }}></th>
                <th style={{ ...sticky.thStyle, padding: '8px 2px', textAlign: 'center', fontWeight: 600, width: 30, color: 'var(--grid-header-fg)' }}></th>
                <th style={{ ...sticky.thStyle, padding: '8px 12px', textAlign: 'left', fontWeight: 600, color: 'var(--grid-header-fg)' }}>Numero</th>
                <th style={{ ...sticky.thStyle, padding: '8px 12px', textAlign: 'left', fontWeight: 600, color: 'var(--grid-header-fg)' }}>Observacao</th>
                <th style={{ ...sticky.thStyle, padding: '8px 12px', textAlign: 'left', fontWeight: 600, color: 'var(--grid-header-fg)' }}>Data</th>
                <th style={{ ...sticky.thStyle, padding: '8px 12px', textAlign: 'left', fontWeight: 600, color: 'var(--grid-header-fg)' }}>Comprador</th>
                <th style={{ ...sticky.thStyle, padding: '8px 12px', textAlign: 'right', fontWeight: 600, color: 'var(--grid-header-fg)' }}>Itens</th>
                <th style={{ ...sticky.thStyle, padding: '8px 12px', textAlign: 'right', fontWeight: 600, color: 'var(--grid-header-fg)' }}>Forn.</th>
                <th style={{ ...sticky.thStyle, padding: '8px 12px', textAlign: 'left', fontWeight: 600, color: 'var(--grid-header-fg)' }}>Status</th>
                <th style={{ ...sticky.thStyle, padding: '8px 12px', textAlign: 'center', fontWeight: 600, width: 60, color: 'var(--grid-header-fg)' }}></th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={10} style={{ padding: 32, textAlign: 'center', color: 'var(--text-muted, #6b7280)' }}>Carregando...</td></tr>
              )}
              {!loading && lista.length === 0 && (
                <tr><td colSpan={10} style={{ padding: 32, textAlign: 'center', color: 'var(--text-muted, #6b7280)' }}>Nenhuma cotacao encontrada.</td></tr>
              )}
              {!loading && lista.map((c, idx) => {
                const sl = STATUS_LABELS[c.status] || { label: c.status, cls: 'badge-cinza' };
                const noTopo = idx === 0;
                const noFim = idx === lista.length - 1;
                const expandido = expandidos.has(c.id);
                const preview = itensPreview[c.id];
                return (
                  <Fragment key={c.id}>
                  <tr
                    style={{
                      borderTop: '1px solid var(--grid-row-border, #f3f4f6)',
                      cursor: 'pointer',
                      background: selecionadaId === c.id ? 'var(--menu-ativo-bg, #dbeafe)' : undefined,
                    }}
                    onClick={() => setSelecionadaId(c.id)}
                    onDoubleClick={() => navigate(rotaDaCotacao(c))}
                    title="1 clique seleciona · 2 cliques abre"
                  >
                    <td style={{ padding: '4px', textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                      {podeReordenar && (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                          <button
                            type="button"
                            onClick={() => void moverCotacao(idx, -1)}
                            disabled={noTopo}
                            style={{ background: 'transparent', border: 'none', padding: 0, display: 'inline-flex', opacity: noTopo ? 0.25 : 1, cursor: noTopo ? 'default' : 'pointer' }}
                            title="Mover para cima"
                          >
                            <ChevronUp size={16} color="var(--primary)" />
                          </button>
                          <button
                            type="button"
                            onClick={() => void moverCotacao(idx, 1)}
                            disabled={noFim}
                            style={{ background: 'transparent', border: 'none', padding: 0, display: 'inline-flex', opacity: noFim ? 0.25 : 1, cursor: noFim ? 'default' : 'pointer' }}
                            title="Mover para baixo"
                          >
                            <ChevronDown size={16} color="var(--primary)" />
                          </button>
                        </div>
                      )}
                    </td>
                    <td style={{ padding: '4px 2px', textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                      <button
                        type="button"
                        onClick={() => void toggleExpand(c.id)}
                        className="btn-icon"
                        aria-label={expandido ? 'Recolher' : 'Expandir'}
                        title={expandido ? 'Ocultar produtos' : 'Ver produtos'}
                      >
                        {expandido ? <Minus size={14} /> : <Plus size={14} />}
                      </button>
                    </td>
                    <td style={{ padding: '8px 12px', fontWeight: 600 }}>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); navigate(rotaDaCotacao(c)); }}
                        style={{ background: 'transparent', border: 'none', color: 'var(--primary)', fontWeight: 600, cursor: 'pointer', padding: 0, textDecoration: 'underline' }}
                        title="Abrir cotacao"
                      >
                        {c.numero}
                      </button>
                      {/* Chip neutro: diz o QUE a cotacao e, nao um estado bom/ruim — cor semantica
                          (amarelo/vermelho/verde) ja tem dono nesta tela (o status). */}
                      {c.tipo === 'informativa' && (
                        <span
                          style={{
                            marginLeft: 8, padding: '1px 6px', borderRadius: 4, fontSize: 11, fontWeight: 700,
                            border: '1px solid var(--panel-border, #d1d5db)', color: 'var(--text-muted, #6b7280)',
                            textTransform: 'uppercase', letterSpacing: 0.3,
                          }}
                          title="Compra informativa — fornecedor unico, sem comparacao de preco"
                        >
                          Informativa
                        </span>
                      )}
                    </td>
                    <td style={{ padding: '8px 12px' }}>{c.observacoes || '—'}</td>
                    <td style={{ padding: '8px 12px' }}>{formatDataSP(c.data_abertura)}</td>
                    <td style={{ padding: '8px 12px' }}>{c.comprador_nome || '—'}</td>
                    <td style={{ padding: '8px 12px', textAlign: 'right' }}>{c.total_itens}</td>
                    <td style={{ padding: '8px 12px', textAlign: 'right' }}>{c.total_fornecedores}</td>
                    <td style={{ padding: '8px 12px' }}>
                      <span className={`badge ${sl.cls}`} style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 4, fontSize: 12, fontWeight: 600 }}>
                        {sl.label}
                      </span>
                    </td>
                    <td style={{ padding: '8px 12px', textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                      <button
                        type="button"
                        onClick={(e) => {
                          if (menuAberto?.id === c.id) { setMenuAberto(null); return; }
                          const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
                          // Ultima linha da lista: sem o flip o painel nasce fora da tela.
                          // Altura estimada = cabecalho + destinos + (divisoria + Excluir) + padding.
                          const qtdDestinos = STATUS_TABS.filter((t) => t.key !== c.status).length;
                          const alturaEstimada = 22 + qtdDestinos * 31 + (isAdmin ? 40 : 0) + 8;
                          const espacoBaixo = window.innerHeight - r.bottom;
                          const abrirCima = espacoBaixo < alturaEstimada + 8 && r.top > espacoBaixo;
                          setMenuAberto({
                            id: c.id,
                            top: abrirCima ? r.top - 4 : r.bottom + 4,
                            left: r.right - 180,
                            abrirCima,
                          });
                        }}
                        title="Acoes"
                        style={{ background: 'transparent', border: 'none', color: 'var(--text-muted, #6b7280)', cursor: 'pointer', padding: 4 }}
                      >
                        <MoreVertical size={16} />
                      </button>
                    </td>
                  </tr>
                  {expandido && (
                    <tr style={{ backgroundColor: 'var(--card-bg)' }}>
                      <td colSpan={10} style={{ padding: '12px 24px' }}>
                        {!preview ? (
                          <span className="muted" style={{ fontSize: 12 }}>Carregando...</span>
                        ) : preview.length === 0 ? (
                          <span className="muted" style={{ fontSize: 12 }}>Nenhum produto</span>
                        ) : (
                          <table style={{ width: '100%', fontSize: 12 }}>
                            <thead>
                              <tr style={{ color: 'var(--text-muted)' }}>
                                <th style={{ textAlign: 'left', padding: 4, width: 28 }}>#</th>
                                <th style={{ width: 36 }}></th>
                                <th style={{ textAlign: 'left', padding: 4, width: 60 }}>Codigo</th>
                                <th style={{ textAlign: 'left', padding: 4 }}>Produto</th>
                                {/* Colunas da linha amarela da grade (menor unitario), com os nomes de la. */}
                                <th style={{ textAlign: 'center', padding: 4, width: 60 }}>Embal.</th>
                                <th style={{ textAlign: 'right', padding: 4, width: 70 }}>Qtd Emb</th>
                                <th style={{ textAlign: 'right', padding: 4, width: 70 }}>Tot Emb</th>
                                <th style={{ textAlign: 'right', padding: 4, width: 90 }}>Preço Emb</th>
                                <th style={{ textAlign: 'right', padding: 4, width: 90 }}>Unitário</th>
                              </tr>
                            </thead>
                            <tbody>
                              {preview.map((it, i) => (
                                <tr key={it.id} style={{ borderTop: '1px solid var(--border)' }}>
                                  <td style={{ padding: 4, color: 'var(--text-muted)' }}>{i + 1}</td>
                                  <td style={{ padding: 4, textAlign: 'center' }}>
                                    {it.foto_url ? <FotoProduto url={it.foto_url} tam={28} style={{ margin: '0 auto' }} /> : <span className="muted">—</span>}
                                  </td>
                                  <td style={{ padding: 4, fontWeight: 700, color: 'var(--text-muted)' }}>{it.codigo ?? '—'}</td>
                                  <td style={{ padding: 4 }}>
                                    {it.descricao}
                                    {/* Mesma regra do "Esconder ja comprei nesta rodada" (DA-368): comprado
                                        depois que a cotacao foi aberta. La dentro, com a caixa marcada, ele some. */}
                                    {it.ultima_compra_em && paraDataMs(it.ultima_compra_em) >= paraDataMs(c.criado_em) && (
                                      <span
                                        style={{ marginLeft: 8, padding: '1px 6px', borderRadius: 4, fontSize: 11, fontWeight: 700, background: '#fde68a', color: '#000' }}
                                        title="Comprado depois que a cotacao foi aberta — dentro da cotacao, o 'Esconder ja comprei nesta rodada' esconde este produto"
                                      >
                                        comprado em {formatDataSP(it.ultima_compra_em)}
                                      </span>
                                    )}
                                  </td>
                                  {it.vencedor ? (
                                    <>
                                      <td style={{ padding: 4, textAlign: 'center', color: 'var(--text-muted)' }}>{it.vencedor.embalagem}</td>
                                      <td style={{ padding: 4, textAlign: 'right' }}>{formatQtd(it.vencedor.quantidade_embalagem)}</td>
                                      <td style={{ padding: 4, textAlign: 'right', fontWeight: 500 }}>{it.vencedor.quantidade_cotada > 0 ? formatQtd(it.vencedor.quantidade_cotada) : '—'}</td>
                                      <td style={{ padding: 4, textAlign: 'right', fontWeight: 700 }}>{formatMoedaSemSimbolo(it.vencedor.preco_emb, 2)}</td>
                                      <td style={{ padding: 4, textAlign: 'right' }}>
                                        <span style={{ padding: '1px 6px', borderRadius: 4, fontWeight: 700, background: '#fde68a', color: '#000' }}>
                                          {formatMoedaSemSimbolo(it.vencedor.unitario, 4)}
                                        </span>
                                      </td>
                                    </>
                                  ) : (
                                    <td colSpan={5} style={{ padding: 4, textAlign: 'right', color: 'var(--text-muted)' }}>sem preço na cotação</td>
                                  )}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                      </td>
                    </tr>
                  )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {menuAberto && (() => {
        const c = lista.find((x) => x.id === menuAberto.id);
        if (!c) return null;
        const destinos = STATUS_TABS.filter((t) => t.key !== c.status);
        return (
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              position: 'fixed',
              top: menuAberto.abrirCima ? undefined : menuAberto.top,
              bottom: menuAberto.abrirCima ? window.innerHeight - menuAberto.top : undefined,
              left: menuAberto.left, width: 180, zIndex: 1000,
              background: 'var(--popup-bg, #fff)', color: 'var(--popup-fg, #1f2937)',
              border: '1px solid var(--popup-border, #e5e7eb)', borderRadius: 6,
              boxShadow: '0 4px 14px rgba(0,0,0,0.15)', padding: '4px 0',
            }}
          >
            <div style={{ padding: '4px 12px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-muted, #9ca3af)' }}>
              Mover para
            </div>
            {destinos.map((t) => (
              <button
                key={t.key}
                type="button"
                onClick={() => mudarStatus(c, t.key)}
                style={{ width: '100%', textAlign: 'left', background: 'transparent', border: 'none', padding: '7px 12px', fontSize: 13, cursor: 'pointer' }}
                onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--menu-ativo-bg, #eff6ff)')}
                onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
              >
                {t.label}
              </button>
            ))}
            {isAdmin && (
              <>
                <div style={{ borderTop: '1px solid var(--popup-border, #e5e7eb)', margin: '4px 0' }} />
                <button
                  type="button"
                  onClick={() => { setMenuAberto(null); setConfirmarRemover(c); }}
                  style={{ width: '100%', textAlign: 'left', background: 'transparent', border: 'none', padding: '7px 12px', fontSize: 13, cursor: 'pointer', color: '#dc2626', display: 'flex', alignItems: 'center', gap: 6 }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = '#fef2f2')}
                  onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                >
                  <Trash2 size={14} /> Excluir
                </button>
              </>
            )}
          </div>
        );
      })()}

      <ConfirmModal
        aberto={!!confirmarRemover}
        titulo="Excluir cotacao?"
        mensagem={confirmarRemover ? `A cotacao ${confirmarRemover.numero} sera removida com TODOS os itens, fornecedores e respostas. Acao irreversivel.` : ''}
        textoConfirmar="Excluir"
        perigoso
        onConfirmar={() => confirmarRemover && remover(confirmarRemover)}
        onCancelar={() => setConfirmarRemover(null)}
      />
    </div>
  );
}
